import {Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {MatAutocompleteModule, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {scrollIntoContainer, scrollOwner} from '@app/util/scroll.helper';

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Pinned above the rest (e.g. the dealer's own country) — tinted so it reads
   *  as the likely answer rather than just the first row (BRIEFD-7094). */
  priority?: boolean;
}

/**
 * Type-to-filter replacement for a long `<select>`. Renders a bare `<input>` so the
 * global input styling in tailwind.scss applies unchanged, and delegates the panel,
 * keyboard navigation and a11y wiring to mat-autocomplete.
 *
 * Not bound to a parent FormControl on purpose: the owning field component keeps its
 * own value format (country select stores `{CODE: name}`), so this only speaks
 * `value`/`valueChange` in plain option values.
 */
@Component({
  selector: 'app-searchable-select',
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatAutocompleteModule]
})
export class SearchableSelectComponent implements OnChanges {

  @Input() options: SearchableSelectOption[] = [];

  @Input() value: string | null = null;

  @Input() placeholder = '';

  @Input() invalid = false;

  @Input() disabled = false;

  /** Show the reset button — off when the field forbids an empty value. */
  @Input() clearable = true;

  @Input() dataField: string | null = null;

  @Input() dataTypeId: string | null = null;

  /** Accessible name for the underlying input (BRIEFD-7272) — the shared
   *  field wrapper's own `<label for>` points at `field.fieldUuid`, which
   *  nothing here carries as an `id`, so callers must pass the field's
   *  visible label text through here instead. */
  @Input() ariaLabel: string | null = null;

  @Output() valueChange = new EventEmitter<string | null>();

  @ViewChild(MatAutocompleteTrigger) private trigger?: MatAutocompleteTrigger;

  private readonly host = inject(ElementRef<HTMLElement>);

  public readonly searchControl = new FormControl<string | SearchableSelectOption>('');

  public filtered: SearchableSelectOption[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled) {
      this.disabled ? this.searchControl.disable({emitEvent: false}) : this.searchControl.enable({emitEvent: false});
    }

    if (changes.options || changes.value) {
      this.filtered = this.options;
      this.showSelected();
    }
  }

  display = (option: string | SearchableSelectOption | null): string => {
    return typeof option === 'string' ? option : (option?.label ?? '');
  };

  filter(term: string): void {
    const needle = term.trim().toLowerCase();

    this.filtered = needle
      ? this.options.filter(option => option.label.toLowerCase().includes(needle))
      : this.options;
  }

  onSelected(option: SearchableSelectOption): void {
    this.valueChange.emit(option.value);
  }

  /**
   * Blank the field when the panel opens so typing starts a fresh search instead of
   * appending to the selected label. Hooked to `opened` rather than `focus` because
   * Material puts focus back on the input after a pick — the next click then opens the
   * panel without ever firing `focus`, which is how "Slovenija" + typing "nem" used to
   * end up as "Slovenijanem".
   *
   * A string in the control means a search is already in progress (the panel reopened
   * mid-typing), so leave that alone; only the selected-option object gets cleared.
   */
  onOpened(): void {
    this.centerField();

    if (typeof this.searchControl.value === 'string') {
      return;
    }

    this.searchControl.setValue('', {emitEvent: false});
    this.filtered = this.options;
  }

  /**
   * Bring the field to the middle of the page when its panel opens, so the 250-row
   * country list has room below it instead of opening off the bottom edge
   * (BRIEFD-7094). `block: 'center'` is a no-op for a field already near the top or
   * bottom of the scroll range, which is the wanted behaviour there.
   */
  private centerField(): void {
    // After Material's own scroll: mat-autocomplete scrolls the field into view
    // once the panel has rendered, which lands the field at the very top of the
    // page. A frame is not enough to be last — theirs happens after that — so
    // wait a beat and scroll instantly, which reads as one movement.
    setTimeout(() => {
      // Only when the panel needs the room. Material places (and flips) the
      // panel itself, so a mid-page dropdown whose list already fits was being
      // centred for nothing — every brand/model select on the advertising form
      // jerked the page by the distance to its own centre.
      if (this.panelFitsInContainer()) {
        return;
      }

      scrollIntoContainer(this.host.nativeElement);
      // The panel was placed against the field's old position; a programmatic
      // scroll doesn't move it on its own.
      this.trigger?.updatePosition();
    }, 150);
  }

  /** Whether the open panel is fully inside the field's own scroll container. */
  private panelFitsInContainer(): boolean {
    const panel = this.trigger?.autocomplete?.panel?.nativeElement;
    const container = scrollOwner(this.host.nativeElement);

    if (!panel || !container) {
      return false;
    }

    const panelRect = panel.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    return panelRect.top >= containerRect.top && panelRect.bottom <= containerRect.bottom;
  }

  /**
   * Blur fires BEFORE the click that picked an option — restoring the field here would
   * cancel that pick outright (the value never changed). So when the panel is still open,
   * leave it alone: its own `closed` event restores after the selection has landed.
   *
   * The guard is also why blur is bound at all: once the filter matches nothing, Material
   * reports the panel as closed without ever emitting `closed`, so `closed` alone would
   * leave the unmatched text sitting in the field.
   */
  onBlur(): void {
    if (this.trigger?.panelOpen) {
      return;
    }

    this.restoreDisplay();
  }

  /** Drop typing that was never confirmed and show the selection again. */
  restoreDisplay(): void {
    this.filtered = this.options;
    this.showSelected();
  }

  clear(): void {
    this.valueChange.emit(null);
  }

  isSelected(option: SearchableSelectOption): boolean {
    return option.value === this.value;
  }

  private showSelected(): void {
    const selected = this.options.find(option => option.value === this.value);

    this.searchControl.setValue(selected ?? '', {emitEvent: false});
  }
}
